/**
 * ReplenishmentProjectionTab — Monthly Replenishment Projections chart + brand breakdown table.
 * Shows calculated replenishment needs (bars) and values (line) per month.
 * Uses proper replenishment logic: rolling stock projection with safety stock,
 * coverage days, MOQ constraints, and per product×store granularity.
 * Filter-reactive via shared ReplenishmentFilterContext.
 */
import React, { useMemo, useState, useRef } from 'react';
import {
  Box, Typography, Paper, CircularProgress, Grid,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import ExportMenu from '../Common/ExportMenu';
import { useReplenishmentFilters } from './ReplenishmentFilterContext';
import { useCurrencyFormat } from '../../hooks/useCurrencyFormat';

const chartTooltipStyle = {
  background: '#FFFFFF',
  border: '1px solid #E5E5E0',
  borderRadius: '6px',
  boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
  fontSize: '12px',
  padding: '8px 12px',
};

const fmtNumber = (v: number) => v.toLocaleString();

interface MonthData {
  month: string;
  month_label: string;
  projected_qty: number;
  projected_value: number;
  source: string;
}

interface BrandRow {
  brand: string;
  months: Record<string, { qty: number; val: number }>;
  total_qty: number;
  total_value: number;
}

interface SkuRow {
  sku: string;
  product_name: string;
  brand: string;
  category: string;
  months: Record<string, { qty: number; val: number }>;
  total_qty: number;
  total_value: number;
}

interface ProjectionResponse {
  monthly: MonthData[];
  brand_breakdown: BrandRow[];
  sku_breakdown: SkuRow[];
}

type DisplayMode = 'qty' | 'val';

const ReplenishmentProjectionTab: React.FC = () => {
  const { brands, categories, stores, cities } = useReplenishmentFilters();
  const { fmtCurrencyCompact: fmtCurrency, fmtCurrencyLabel } = useCurrencyFormat();
  const [displayMode, setDisplayMode] = useState<DisplayMode>('qty');
  const chartContainerRef = useRef<HTMLDivElement>(null);

  const { data, isLoading } = useQuery<ProjectionResponse>({
    queryKey: ['monthly-purchase-projections', brands, categories, stores, cities],
    queryFn: () =>
      api.getMonthlyPurchaseProjections({
        brands: brands.length > 0 ? brands.join(',') : undefined,
        categories: categories.length > 0 ? categories.join(',') : undefined,
        cities: cities.length > 0 ? cities.join(',') : undefined,
        stores: stores.length > 0 ? stores.join(',') : undefined,
      }),
    refetchInterval: 300000,
    retry: 1,
  });

  const monthlyData = data?.monthly || [];
  const brandBreakdown = data?.brand_breakdown || [];
  const skuBreakdown = data?.sku_breakdown || [];

  const chartData = useMemo(() => {
    return monthlyData.map((d) => ({
      ...d,
      label: d.month_label?.split(' ')[0] || d.month,
    }));
  }, [monthlyData]);

  const totals = useMemo(() => {
    if (!chartData.length) return { qty: 0, value: 0, months: 0, avgMonthly: 0 };
    const qty = chartData.reduce((s, d) => s + (d.projected_qty || 0), 0);
    const value = chartData.reduce((s, d) => s + (d.projected_value || 0), 0);
    const activeMonths = chartData.filter((d) => d.projected_qty > 0).length;
    return { qty, value, months: activeMonths, avgMonthly: activeMonths > 0 ? value / activeMonths : 0 };
  }, [chartData]);

  // Month keys for table columns
  const monthKeys = useMemo(() => monthlyData.map((d) => d.month), [monthlyData]);
  const monthLabels = useMemo(() => monthlyData.map((d) => d.month_label?.split(' ')[0] || d.month), [monthlyData]);

  // Grand totals for table footer
  const grandTotalQty = useMemo(() => brandBreakdown.reduce((s, b) => s + b.total_qty, 0), [brandBreakdown]);
  const grandTotalVal = useMemo(() => brandBreakdown.reduce((s, b) => s + b.total_value, 0), [brandBreakdown]);

  // Flatten SKU breakdown into rows for <ExportMenu>.
  const exportRows = useMemo(() => {
    if (!skuBreakdown.length || !monthKeys.length) return [];

    const rows: Record<string, any>[] = skuBreakdown.map((row) => {
      const base: Record<string, any> = {
        SKU: row.sku,
        'Product Name': row.product_name,
        Brand: row.brand,
        Category: row.category,
      };
      monthKeys.forEach((mk, i) => {
        const cell = row.months[mk] || { qty: 0, val: 0 };
        base[`${monthLabels[i]} Qty`] = cell.qty;
        base[`${monthLabels[i]} Value ${fmtCurrencyLabel()}`] = cell.val;
      });
      base['Total Qty'] = row.total_qty;
      base[`Total Value ${fmtCurrencyLabel()}`] = row.total_value;
      return base;
    });

    // Append grand totals row
    const totalRow: Record<string, any> = {
      SKU: 'TOTAL',
      'Product Name': '',
      Brand: '',
      Category: '',
    };
    monthKeys.forEach((mk, i) => {
      const mQty = skuBreakdown.reduce((s, r) => s + (r.months[mk]?.qty || 0), 0);
      const mVal = skuBreakdown.reduce((s, r) => s + (r.months[mk]?.val || 0), 0);
      totalRow[`${monthLabels[i]} Qty`] = mQty;
      totalRow[`${monthLabels[i]} Value ${fmtCurrencyLabel()}`] = mVal;
    });
    totalRow['Total Qty'] = skuBreakdown.reduce((s, r) => s + r.total_qty, 0);
    totalRow[`Total Value ${fmtCurrencyLabel()}`] = skuBreakdown.reduce((s, r) => s + r.total_value, 0);
    rows.push(totalRow);
    return rows;
  }, [skuBreakdown, monthKeys, monthLabels, fmtCurrencyLabel]);

  return (
    <Box>
      {/* Display Mode Toggle + Export */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase' }}>
          Display
        </Typography>
        <ToggleButtonGroup
          value={displayMode}
          exclusive
          onChange={(_, v) => { if (v) setDisplayMode(v); }}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              px: 2, py: 0.5, fontSize: '11px', fontWeight: 600,
              textTransform: 'none', border: '1px solid var(--color-border)',
              color: 'var(--color-text-muted)',
              '&.Mui-selected': {
                bgcolor: '#1A1A1A', color: '#fff',
                '&:hover': { bgcolor: '#333' },
              },
            },
          }}
        >
          <ToggleButton value="qty">Quantity</ToggleButton>
          <ToggleButton value="val">Value {fmtCurrencyLabel()}</ToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ flex: 1 }} />

        <ExportMenu
          data={exportRows}
          filename="Replenishment_Projection"
          title="Monthly Replenishment Projections"
          sheetName="Projections"
          chartRef={chartContainerRef}
        />
      </Box>

      {/* KPI Summary */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total Replenishment Qty', value: fmtNumber(totals.qty), color: 'var(--color-accent)' },
          { label: 'Total Replenishment Value', value: fmtCurrency(totals.value), color: 'var(--color-success)' },
          { label: 'Active Months', value: `${totals.months}`, color: 'var(--color-info)' },
          { label: 'Avg Monthly Value', value: fmtCurrency(totals.avgMonthly), color: 'var(--color-warning)' },
        ].map((kpi) => (
          <Grid item xs={6} md={3} key={kpi.label}>
            <Paper sx={{ p: '16px 20px', border: '1px solid var(--color-border)' }}>
              <Typography sx={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', color: 'var(--color-text-muted)', mb: 0.5 }}>
                {kpi.label}
              </Typography>
              <Typography sx={{ fontSize: 'var(--text-lg)', fontWeight: 700, color: 'var(--color-text-primary)', fontVariantNumeric: 'tabular-nums' }}>
                {isLoading ? <CircularProgress size={16} /> : kpi.value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Main Chart */}
      <Paper ref={chartContainerRef} sx={{ p: '20px', border: '1px solid var(--color-border)' }}>
        <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 600, mb: 0.5 }}>
          Monthly Replenishment Projections
        </Typography>
        <Typography sx={{ fontSize: '10px', color: 'var(--color-text-muted)', mb: 2 }}>
          <Box component="span" sx={{ display: 'inline-block', width: 10, height: 10, bgcolor: '#C9A96E', borderRadius: '2px', mr: 0.5, verticalAlign: 'middle' }} /> Replenishment Quantity
          {' \u00B7 '}
          <Box component="span" sx={{ display: 'inline-block', width: 10, height: 3, bgcolor: '#8B2438', borderRadius: '1px', mr: 0.5, verticalAlign: 'middle' }} /> Replenishment Value {fmtCurrencyLabel()}
          {' \u00B7 '}
          Includes safety stock, coverage days, MOQ constraints
        </Typography>

        {isLoading ? (
          <Box sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={400}>
            <ComposedChart data={chartData} margin={{ left: 8, right: 16, top: 20, bottom: 5 }} barCategoryGap="20%">
              <CartesianGrid strokeDasharray="3 3" stroke="#EFEFEA" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: '#8A8A85' }}
                tickLine={false}
                axisLine={{ stroke: '#E5E5E0' }}
              />
              <YAxis
                yAxisId="qty"
                orientation="left"
                tick={{ fontSize: 10, fill: '#8A8A85' }}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v: number) => v >= 1000 ? `${(v / 1000).toFixed(0)}K` : `${v}`}
                label={{ value: 'Quantity', angle: -90, position: 'insideLeft', offset: -5, style: { fontSize: 10, fill: '#8A8A85' } }}
              />
              <YAxis
                yAxisId="val"
                orientation="right"
                tick={{ fontSize: 10, fill: '#8A8A85' }}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v: number) => v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}K` : `${v}`}
                label={{ value: `Value ${fmtCurrencyLabel()}`, angle: 90, position: 'insideRight', offset: -5, style: { fontSize: 10, fill: '#8A8A85' } }}
              />
              <RTooltip
                formatter={(value: number, name: string) => {
                  if (name === 'Projected Qty') return [fmtNumber(value), name];
                  return [fmtCurrency(value), name];
                }}
                contentStyle={chartTooltipStyle}
                labelFormatter={(label: string) => label}
              />
              <Legend wrapperStyle={{ fontSize: '11px' }} />
              <Bar yAxisId="qty" dataKey="projected_qty" name="Projected Qty" fill="#C9A96E" radius={[3, 3, 0, 0]} barSize={32}>
                {chartData.map((entry: any, idx: number) => (
                  <Cell key={idx} fill={entry.source === 'orders' ? '#8B7355' : entry.source === 'mixed' ? '#B08C50' : '#C9A96E'} />
                ))}
              </Bar>
              <Line
                yAxisId="val"
                type="monotone"
                dataKey="projected_value"
                name="Projected Value"
                stroke="#8B2438"
                strokeWidth={2}
                dot={{ r: 4, fill: '#8B2438', stroke: '#fff', strokeWidth: 2 }}
                activeDot={{ r: 6 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <Box sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ color: 'var(--color-text-muted)' }}>No replenishment projection data available</Typography>
          </Box>
        )}
      </Paper>

      {/* Brand Breakdown Table */}
      {!isLoading && brandBreakdown.length > 0 && (
        <Paper sx={{ mt: 3, border: '1px solid var(--color-border)', overflow: 'hidden' }}>
          <Box sx={{ p: '16px 20px 0' }}>
            <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 600, mb: 0.25 }}>
              Replenishment by Brand
            </Typography>
            <Typography sx={{ fontSize: '10px', color: 'var(--color-text-muted)', mb: 1.5 }}>
              Quantities and values {fmtCurrencyLabel()} per brand per month
            </Typography>
          </Box>
          <TableContainer sx={{ maxHeight: 520 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: '11px',
                      bgcolor: 'var(--color-bg-secondary)',
                      borderBottom: '2px solid var(--color-border)',
                      position: 'sticky',
                      left: 0,
                      zIndex: 3,
                      minWidth: 140,
                    }}
                  >
                    Brand
                  </TableCell>
                  {monthLabels.map((label, i) => (
                    <TableCell
                      key={monthKeys[i]}
                      align="right"
                      sx={{
                        fontWeight: 600,
                        fontSize: '10px',
                        bgcolor: 'var(--color-bg-secondary)',
                        borderBottom: '2px solid var(--color-border)',
                        whiteSpace: 'nowrap',
                        minWidth: 90,
                      }}
                    >
                      {label}
                    </TableCell>
                  ))}
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 700,
                      fontSize: '11px',
                      bgcolor: 'var(--color-bg-secondary)',
                      borderBottom: '2px solid var(--color-border)',
                      minWidth: 110,
                    }}
                  >
                    Total
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {brandBreakdown.map((row) => (
                  <TableRow key={row.brand} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontSize: '11px',
                        position: 'sticky',
                        left: 0,
                        bgcolor: '#fff',
                        zIndex: 1,
                        borderRight: '1px solid var(--color-border)',
                      }}
                    >
                      {row.brand}
                    </TableCell>
                    {monthKeys.map((mk) => {
                      const cell = row.months[mk] || { qty: 0, val: 0 };
                      return (
                        <TableCell key={mk} align="right" sx={{ py: '6px', px: '8px' }}>
                          {cell.qty > 0 ? (
                            <Box>
                              <Typography sx={{ fontSize: '11px', fontWeight: 600, fontVariantNumeric: 'tabular-nums', lineHeight: 1.3 }}>
                                {fmtNumber(cell.qty)}
                              </Typography>
                              <Typography sx={{ fontSize: '9px', color: 'var(--color-text-muted)', fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}>
                                {fmtCurrency(cell.val)}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography sx={{ fontSize: '11px', color: '#D0D0CB' }}>—</Typography>
                          )}
                        </TableCell>
                      );
                    })}
                    <TableCell
                      align="right"
                      sx={{
                        py: '6px',
                        px: '8px',
                        borderLeft: '1px solid var(--color-border)',
                        bgcolor: 'var(--color-bg-secondary)',
                      }}
                    >
                      <Typography sx={{ fontSize: '11px', fontWeight: 700, fontVariantNumeric: 'tabular-nums', lineHeight: 1.3 }}>
                        {fmtNumber(row.total_qty)}
                      </Typography>
                      <Typography sx={{ fontSize: '9px', color: 'var(--color-text-muted)', fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}>
                        {fmtCurrency(row.total_value)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
                {/* Grand Total Row */}
                <TableRow sx={{ bgcolor: 'var(--color-bg-secondary)' }}>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: '11px',
                      position: 'sticky',
                      left: 0,
                      bgcolor: 'var(--color-bg-secondary)',
                      zIndex: 1,
                      borderRight: '1px solid var(--color-border)',
                      borderTop: '2px solid var(--color-border)',
                    }}
                  >
                    Total
                  </TableCell>
                  {monthKeys.map((mk) => {
                    const mQty = brandBreakdown.reduce((s, b) => s + (b.months[mk]?.qty || 0), 0);
                    const mVal = brandBreakdown.reduce((s, b) => s + (b.months[mk]?.val || 0), 0);
                    return (
                      <TableCell
                        key={mk}
                        align="right"
                        sx={{ py: '6px', px: '8px', borderTop: '2px solid var(--color-border)' }}
                      >
                        <Typography sx={{ fontSize: '11px', fontWeight: 700, fontVariantNumeric: 'tabular-nums', lineHeight: 1.3 }}>
                          {fmtNumber(mQty)}
                        </Typography>
                        <Typography sx={{ fontSize: '9px', color: 'var(--color-text-muted)', fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}>
                          {fmtCurrency(mVal)}
                        </Typography>
                      </TableCell>
                    );
                  })}
                  <TableCell
                    align="right"
                    sx={{
                      py: '6px',
                      px: '8px',
                      borderLeft: '1px solid var(--color-border)',
                      borderTop: '2px solid var(--color-border)',
                    }}
                  >
                    <Typography sx={{ fontSize: '11px', fontWeight: 700, fontVariantNumeric: 'tabular-nums', lineHeight: 1.3 }}>
                      {fmtNumber(grandTotalQty)}
                    </Typography>
                    <Typography sx={{ fontSize: '9px', color: 'var(--color-text-muted)', fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}>
                      {fmtCurrency(grandTotalVal)}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
      {/* SKU Detail Table */}
      {!isLoading && skuBreakdown.length > 0 && (
        <Paper sx={{ mt: 3, border: '1px solid var(--color-border)', overflow: 'hidden' }}>
          <Box sx={{ p: '16px 20px 0' }}>
            <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 600, mb: 0.25 }}>
              Replenishment by SKU
            </Typography>
            <Typography sx={{ fontSize: '10px', color: 'var(--color-text-muted)', mb: 1.5 }}>
              {displayMode === 'qty' ? 'Quantities' : `Values ${fmtCurrencyLabel()}`} per SKU per month — {skuBreakdown.length} SKUs with replenishment needs
            </Typography>
          </Box>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 700, fontSize: '11px',
                      bgcolor: 'var(--color-bg-secondary)',
                      borderBottom: '2px solid var(--color-border)',
                      position: 'sticky', left: 0, zIndex: 3, minWidth: 200,
                    }}
                  >
                    SKU
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600, fontSize: '10px',
                      bgcolor: 'var(--color-bg-secondary)',
                      borderBottom: '2px solid var(--color-border)',
                      minWidth: 90,
                    }}
                  >
                    Brand
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600, fontSize: '10px',
                      bgcolor: 'var(--color-bg-secondary)',
                      borderBottom: '2px solid var(--color-border)',
                      minWidth: 80,
                    }}
                  >
                    Category
                  </TableCell>
                  {monthLabels.map((label, i) => (
                    <TableCell
                      key={monthKeys[i]}
                      align="right"
                      sx={{
                        fontWeight: 600, fontSize: '10px',
                        bgcolor: 'var(--color-bg-secondary)',
                        borderBottom: '2px solid var(--color-border)',
                        whiteSpace: 'nowrap', minWidth: 80,
                      }}
                    >
                      {label}
                    </TableCell>
                  ))}
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 700, fontSize: '11px',
                      bgcolor: 'var(--color-bg-secondary)',
                      borderBottom: '2px solid var(--color-border)',
                      minWidth: 100,
                    }}
                  >
                    Total
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {skuBreakdown.map((row) => (
                  <TableRow key={row.sku} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                    <TableCell
                      sx={{
                        fontSize: '11px', position: 'sticky', left: 0,
                        bgcolor: '#fff', zIndex: 1,
                        borderRight: '1px solid var(--color-border)',
                        maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis',
                      }}
                    >
                      <Typography sx={{ fontSize: '11px', fontWeight: 600, lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {row.product_name}
                      </Typography>
                      <Typography sx={{ fontSize: '9px', color: 'var(--color-text-muted)', lineHeight: 1.2 }}>
                        {row.sku}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: '10px', color: 'var(--color-text-muted)' }}>{row.brand}</TableCell>
                    <TableCell sx={{ fontSize: '10px', color: 'var(--color-text-muted)' }}>{row.category}</TableCell>
                    {monthKeys.map((mk) => {
                      const cell = row.months[mk] || { qty: 0, val: 0 };
                      const val = displayMode === 'qty' ? cell.qty : cell.val;
                      return (
                        <TableCell key={mk} align="right" sx={{ py: '6px', px: '8px' }}>
                          {val > 0 ? (
                            <Typography sx={{ fontSize: '11px', fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
                              {displayMode === 'qty' ? fmtNumber(val) : fmtCurrency(val)}
                            </Typography>
                          ) : (
                            <Typography sx={{ fontSize: '11px', color: '#D0D0CB' }}>—</Typography>
                          )}
                        </TableCell>
                      );
                    })}
                    <TableCell
                      align="right"
                      sx={{
                        py: '6px', px: '8px',
                        borderLeft: '1px solid var(--color-border)',
                        bgcolor: 'var(--color-bg-secondary)',
                      }}
                    >
                      <Typography sx={{ fontSize: '11px', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                        {displayMode === 'qty' ? fmtNumber(row.total_qty) : fmtCurrency(row.total_value)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
                {/* Grand Total Row */}
                <TableRow sx={{ bgcolor: 'var(--color-bg-secondary)' }}>
                  <TableCell
                    colSpan={3}
                    sx={{
                      fontWeight: 700, fontSize: '11px',
                      position: 'sticky', left: 0,
                      bgcolor: 'var(--color-bg-secondary)', zIndex: 1,
                      borderRight: '1px solid var(--color-border)',
                      borderTop: '2px solid var(--color-border)',
                    }}
                  >
                    Total ({skuBreakdown.length} SKUs)
                  </TableCell>
                  {monthKeys.map((mk) => {
                    const mVal = skuBreakdown.reduce((s, r) => s + (displayMode === 'qty' ? (r.months[mk]?.qty || 0) : (r.months[mk]?.val || 0)), 0);
                    return (
                      <TableCell
                        key={mk}
                        align="right"
                        sx={{ py: '6px', px: '8px', borderTop: '2px solid var(--color-border)' }}
                      >
                        <Typography sx={{ fontSize: '11px', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                          {displayMode === 'qty' ? fmtNumber(mVal) : fmtCurrency(mVal)}
                        </Typography>
                      </TableCell>
                    );
                  })}
                  <TableCell
                    align="right"
                    sx={{
                      py: '6px', px: '8px',
                      borderLeft: '1px solid var(--color-border)',
                      borderTop: '2px solid var(--color-border)',
                    }}
                  >
                    <Typography sx={{ fontSize: '11px', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                      {displayMode === 'qty'
                        ? fmtNumber(skuBreakdown.reduce((s, r) => s + r.total_qty, 0))
                        : fmtCurrency(skuBreakdown.reduce((s, r) => s + r.total_value, 0))}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

export default ReplenishmentProjectionTab;
